/**
 * Permission System for Role-Based Access Control
 *
 * This utility provides functions to check permissions for delegated users
 * and client employees based on their assigned permissions.
 */

/**
 * Default permission sets for different roles
 */
export const DEFAULT_PERMISSIONS = {
  // Coach Dashboard Permissions
  coach: {
    // Sessions
    sessions: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      manageCalendar: true,
    },
    // Clients
    clients: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      assign: true,
    },
    // Resources
    resources: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      templates: true,
      knowledgeBase: true,
      bestPractices: true,
      coachingTools: true,
    },
    // CRM
    crm: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      notes: true,
      assessments: true,
      tasks: true,
    },
    // Reports
    reports: {
      view: true,
      create: true,
      export: true,
    },
    // Team Management
    team: {
      view: true,
      manage: true,
    },
    // Settings
    settings: {
      view: true,
      edit: true,
    },
  },
  // Client Dashboard Permissions
  client: {
    // Profile
    profile: {
      view: true,
      edit: true,
    },
    // Documents
    documents: {
      view: true,
      upload: true,
      delete: false,
    },
    // Applications
    applications: {
      view: true,
      create: false,
      edit: false,
    },
    // Programs
    programs: {
      view: true,
      enroll: false,
    },
    // Training modules (granular for employees)
    training: {
      view: true,
      designABusiness: true,
      costingAndManagement: true,
      productDevelopment: false,
      commercialisation: false,
      scaling: false,
      funding: false,
    },
    // Reports
    reports: {
      view: true,
      export: false,
    },
    // Team & Employees
    team: {
      view: true,
      manage: false,
      addEmployees: false,
      editPermissions: false,
    },
    // Profiling (Personality assessment - for founders)
    profiling: {
      view: false,
      personalityAssessment: false,
      mindsetAssessment: false,
      businessHealthCheck: false,
    },
    // Settings
    settings: {
      view: true,
      edit: false,
    },
  },
};

/**
 * Check if user has permission for a specific action
 * @param {Object} userPermissions - User's permission object
 * @param {string} category - Permission category (e.g., 'sessions', 'clients')
 * @param {string} action - Specific action (e.g., 'view', 'create', 'edit', 'delete')
 * @returns {boolean} - Whether user has permission
 */
export const hasPermission = (userPermissions, category, action) => {
  if (!userPermissions) return false;

  // If permissions are disabled or not set, deny access
  if (userPermissions.enabled === false) return false;

  // Check if category exists
  if (!userPermissions[category]) return false;

  // Check if action is allowed
  return userPermissions[category][action] === true;
};

/**
 * Check if user can access a specific section
 * @param {Object} userPermissions - User's permission object
 * @param {string} section - Section name (e.g., 'sessions', 'clients')
 * @returns {boolean} - Whether user can access the section
 */
export const canAccessSection = (userPermissions, section) => {
  if (!userPermissions) return false;
  if (userPermissions.enabled === false) return false;

  // Check if section exists and has at least view permission
  return hasPermission(userPermissions, section, 'view');
};

/**
 * Get all allowed sections for a user
 * @param {Object} userPermissions - User's permission object
 * @returns {string[]} - Array of allowed section names
 */
export const getAllowedSections = userPermissions => {
  if (!userPermissions || userPermissions.enabled === false) return [];

  return Object.keys(userPermissions).filter(
    key => key !== 'enabled' && canAccessSection(userPermissions, key)
  );
};

/**
 * Merge permissions with defaults
 * @param {Object} customPermissions - Custom permissions from user
 * @param {string} roleType - 'coach' or 'client'
 * @returns {Object} - Merged permissions object
 */
export const mergePermissions = (customPermissions, roleType = 'coach') => {
  const defaults = DEFAULT_PERMISSIONS[roleType] || DEFAULT_PERMISSIONS.coach;

  if (!customPermissions) {
    return { enabled: true, ...defaults };
  }

  // Deep merge custom permissions with defaults
  const merged = { enabled: customPermissions.enabled !== false, ...defaults };

  Object.keys(customPermissions).forEach(category => {
    if (category === 'enabled') return;

    if (merged[category]) {
      merged[category] = {
        ...merged[category],
        ...customPermissions[category],
      };
    } else {
      merged[category] = customPermissions[category];
    }
  });

  return merged;
};

/**
 * Validate permission structure
 * @param {Object} permissions - Permissions object to validate
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validatePermissions = permissions => {
  const errors = [];

  if (!permissions || typeof permissions !== 'object') {
    errors.push('Permissions must be an object');
    return { valid: false, errors };
  }

  // Check for enabled flag
  if (permissions.enabled !== undefined && typeof permissions.enabled !== 'boolean') {
    errors.push('enabled must be a boolean');
  }

  // Validate each category
  Object.keys(permissions).forEach(category => {
    if (category === 'enabled') return;

    if (typeof permissions[category] !== 'object') {
      errors.push(`${category} must be an object`);
      return;
    }

    // Validate actions within category
    Object.keys(permissions[category]).forEach(action => {
      if (typeof permissions[category][action] !== 'boolean') {
        errors.push(`${category}.${action} must be a boolean`);
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
